/**
 * ╔══════════════════════════════════════════════════════════════╗
 * ║  ⚠️  DEPRECATED — V2 战斗期已改用 PyodideDualRuntime      ║
 * ║                                                            ║
 * ║  保留此文件仅供 preflight 阶段使用（preflight 不需要物理   ║
 * ║  隔离，池复用即可）。V2 战斗期走 pyodide-dual-runtime.ts    ║
 * ║  的 2 常驻 Worker 物理隔离模式。                           ║
 * ║                                                            ║
 * ║  后续多 Player 场景（3v3/5v5）可能需要重新评估池的设计。   ║
 * ║  届时可考虑从 Map<BotId, Worker> 扩展到池模式。            ║
 * ╚══════════════════════════════════════════════════════════════╝
 *
 * BattleCode Demo v1.0.0 — Pyodide Worker 单例池
 *
 * P1-#4 修复（2026-07-04）：
 *   之前每次 `new PyodideWorker()` 都重新 `await import("https://cdn.jsdelivr.net/.../pyodide.mjs")`
 *   + `loadPyodide()`（3-15s）。屏 2 Test + 屏 4 战斗至少 3 次冷启动，DevTools Network 看到 3 次下载。
 *
 *   现在用 acquire/release 池：
 *     - release(worker) 把 worker 还到池里（Pyodide 实例保留在 Worker 内存中）
 *     - acquire() 优先从池里取热 worker，池空才 new PyodideWorker()（冷启动）
 *     - Pyodide 0.26 的 mjs 文件本身有 HTTP 缓存（CDN 强缓存 1 天），所以浏览器层面
 *       已经只下载 1 次；Worker 池主要是省 `loadPyodide()` 的 WASM init 时间（~1-2s）
 *
 * 约束（demo 阶段简化）：
 *   - 池容量上限 4（2 战斗 worker + 2 preflight 备用）
 *   - Worker 一旦出 init 错误就 terminate 不还池（不污染池）
 *   - 不预热（lazy 创建），玩家第一次 Test / 第一次战斗必冷启动（无法避免）
 *
 * 后续 v1+ 可考虑：
 *   - 启动时后台 `warmup()` 1 个 worker
 *   - SharedWorker 跨标签页共享（Safari 不支持，需降级）
 */

import PyodideWorker from "./pyodide-worker?worker";

const POOL_MAX = 4;
const pool: Worker[] = [];
let acquired = 0; // 已 acquire 未 release 的 worker 数

/**
 * 取一个热 worker（优先）或新 worker（冷启动）
 * 调用方负责 `postMessage({ type: "init", ... })` 重新初始化
 */
export function acquireWorker(): Worker {
  acquired++;
  const warm = pool.pop();
  if (warm) {
    return warm;
  }
  // 冷启动
  const w = new PyodideWorker();
  // 2026-07-05 修复(P0-#12 后续 · 诊断静默三连之一):
  //   acquireWorker 创建 worker 后没挂 onerror → worker import 阶段 throw 主线程无感知
  //   加 onerror + messageerror 监听,主线程 console 立刻看到,不再"静默 5s 后 init timeout"
  w.addEventListener("error", (e) => {
    console.error("[BC-DIAG] pyodide-worker onerror:", e.message, e.filename, e.lineno);
  });
  w.addEventListener("messageerror", (e) => {
    console.error("[BC-DIAG] pyodide-worker messageerror:", e);
  });
  return w;
}

/**
 * 归还 worker。worker 的 Pyodide 实例保留，下次 acquire 可直接复用。
 * @param healthy false 表示 worker 状态已坏（init 失败 / runtime 崩溃），会被 terminate 不入池
 */
export function releaseWorker(worker: Worker, healthy: boolean = true): void {
  acquired--;
  if (!healthy) {
    worker.terminate();
    return;
  }
  if (pool.length >= POOL_MAX) {
    worker.terminate();
    return;
  }
  pool.push(worker);
}

/** 调试用：当前池状态 */
export function getPoolStats(): { idle: number; acquired: number; max: number } {
  return { idle: pool.length, acquired, max: POOL_MAX };
}

/** 测试/调试用：清空池（不常用） */
export function drainPool(): void {
  while (pool.length > 0) {
    const w = pool.pop();
    w?.terminate();
  }
}

/**
 * 启动期预热 N 个 Pyodide Worker(2026-07-05 新增,P0-#12 后续)
 *
 * 屏 1 渲染后调一次,触发 Worker 启动 + loadPyodideFromCdn()。
 * Worker ready 后留在池里,屏 2 preflight / 屏 4 战斗 acquire 即可秒得热 worker。
 *
 * 不阻塞屏 1 渲染:走 setTimeout(0)。失败也不抛(后台 log)。
 *
 * 2026-07-05 修正:
 *  - Worker 发 "ready" 事件时(loadPyodideFromCdn 完成)立即 release,不等 30s
 *  - 默认预热 2 个(preflight 并行跑 player + AI 各 1 个,缺一会冷启动 → 30s timeout)
 *  - ready 时在主线程 worker 对象上挂 __pyodideReady 标志(Worker self 属性跨 boundary 不可见)
 */
const WARMUP_COUNT = 2;
let warmupInFlight = false;
let warmupDoneCount = 0;
export function warmupPyodide(): void {
  if (warmupInFlight) return;
  if (pool.length >= WARMUP_COUNT) return; // 已经有足够热 worker
  warmupInFlight = true;
  setTimeout(() => {
    try {
      for (let i = 0; i < WARMUP_COUNT; i++) {
        const w = acquireWorker();
        const handler = (e: MessageEvent) => {
          if (e.data?.type === "ready") {
            w.removeEventListener("message", handler);
            // 关键:在主线程 worker 对象上挂 __pyodideReady=true(Worker self 属性跨 boundary 不可见)
            (w as any).__pyodideReady = true;
            releaseWorker(w, true);
            warmupDoneCount++;
            if (warmupDoneCount >= WARMUP_COUNT) {
              warmupInFlight = false;
            }
          }
        };
        w.addEventListener("message", handler);
        // 安全网:30s 后无论如何 release,避免 warmup 卡死
        setTimeout(() => {
          w.removeEventListener("message", handler);
          if (warmupDoneCount < WARMUP_COUNT) {
            // 2026-07-05 修复(T47.8 根因 · 池污染修复):
            //   之前安全网里 releaseWorker(w, true) 把"没收到 ready"的坏 worker 当 healthy 还池
            //   根因是 worker self.onmessage 没挂上(Pyodide init 失败走 catch return)
            //   → 主线程 acquire 出来"假 warm"worker → initPyodideInWorker 看到 __pyodideReady=true 立刻 return
            //   → 发 init → worker 没人处理 → init_done 永远不发 → 5s waitForInit timeout
            //   修复:安全网里 releaseWorker(w, false) — unhealthy → terminate,不污染池
            console.warn(`[BC-DIAG] warmupPyodide safety-net: worker ${i} not ready in 30s, terminating (unhealthy)`);
            releaseWorker(w, false);
            warmupDoneCount++;
            if (warmupDoneCount >= WARMUP_COUNT) {
              warmupInFlight = false;
            }
          }
        }, 30_000);
      }
    } catch (e) {
      warmupInFlight = false;
      // 不抛,后台失败不阻塞屏 1
      console.warn("[pyodide-pool] warmup failed:", e);
    }
  }, 0);
}
